import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  subscribeRealtime,
  type RealtimeEnvelope,
} from '../lib/realtime';

type Matcher = string | RegExp | ((channel: string) => boolean);

/**
 * Subscribe a component to realtime envelopes matching `matcher`.
 *
 * `matcher` accepts:
 *   - exact channel string: `"user:abc:trade"`
 *   - prefix wildcard:      `"user:abc:*"` (anything ending in `:*`)
 *   - regex                 `/^user:.*:trade$/`
 *   - predicate             `(ch) => ch.includes('global')`
 *
 * Auto-unsubscribes on unmount. The subscription is opened once per
 * matcher (no re-binding on every render) but the handler IS kept
 * fresh via a ref — so callbacks that close over props / state see
 * the latest values without the caller having to memo aggressively.
 * Previously this was "pass a stable callback or accept stale-closure"
 * which silently bit OceanStrip (Codex PR #329 P2) + the headless
 * ActiveTrade inside PriceToBeat (P1).
 */
export function useRealtime(
  matcher: Matcher,
  handler: (env: RealtimeEnvelope) => void,
): void {
  const handlerRef = useRef(handler);
  // Layout effect to ensure the ref is up-to-date before any realtime
  // event fires in this commit's flush window.
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    const unsub = subscribeRealtime(matcher, (env) => handlerRef.current(env));
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeof matcher === 'string' ? matcher : matcher.toString()]);
}

/**
 * Hook the realtime stream into React Query: when an event for
 * `user:*:{kind}` arrives, invalidate the matching cache key. Mounted
 * once near the app root so every page benefits without per-page wiring.
 *
 * The mapping is deliberate (not auto-derived) because not every event
 * kind has a 1:1 query — `notify` for example is consumed directly by
 * the toast/notifications page, not by invalidating a cache.
 */
export function useRealtimeInvalidator(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const unsubs: Array<() => void> = [];
    const invalidate = (keyPrefix: readonly unknown[]) =>
      qc.invalidateQueries({ queryKey: keyPrefix });

    unsubs.push(
      subscribeRealtime(
        (ch) => ch.endsWith(':balance'),
        () => invalidate(['balance']),
      ),
    );
    unsubs.push(
      subscribeRealtime(
        (ch) => ch.endsWith(':trade'),
        () => {
          // ['positions', ...] — RQ matches by prefix, so all variants
          // (status / bot_kind partitioned) get refetched together.
          invalidate(['positions']);
          invalidate(['activity']);
          invalidate(['balance']);
        },
      ),
    );
    unsubs.push(
      subscribeRealtime(
        (ch) => ch.endsWith(':wallet'),
        () => {
          invalidate(['deposits']);
          invalidate(['withdrawals']);
          invalidate(['activity']);
          invalidate(['balance']);
        },
      ),
    );
    unsubs.push(
      subscribeRealtime(
        (ch) => ch.endsWith(':notify'),
        (env) => {
          // Bot start/stop changes /v1/me's bot_enabled. A kill_switch
          // event can flip a global flag (`/v1/platform-config`) or a
          // per-user one (`/v1/me` `kill_switches`) — refetch both so
          // the banner reflects either. Other notify types
          // (fee_charged, …) are consumed by the toast / notifications
          // page, not by cache invalidation.
          if (env.type === 'bot_state') invalidate(['me']);
          if (env.type === 'kill_switch') {
            invalidate(['platform-config']);
            invalidate(['me']);
          }
        },
      ),
    );
    unsubs.push(
      subscribeRealtime(
        (ch) => ch === 'global:kill_switch',
        () => invalidate(['platform-config']),
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [qc]);
}
